import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PortalV2Layout from './PortalV2Layout';
import ClientJobsMap from './ClientJobsMap';
import AnimatedNumber from './AnimatedNumber';
import {
  FileText, Clock, CheckCircle, AlertCircle, PlusCircle, ArrowRight, RefreshCw,
  MapPin, Calendar, Archive, Activity, Inbox, XCircle, DollarSign, CreditCard,
  Gift, Copy, Users,
} from 'lucide-react';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const ClientV2Dashboard = () => {
  const { user, authFetch } = useAuth();
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, non_served: 0, success_rate: 0 });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [outstanding, setOutstanding] = useState({ total: 0, count: 0 });
  const [referral, setReferral] = useState({ referral_code: '', referral_credit: 0, total_referrals: 0, total_saved: 0 });
  const [codeCopied, setCodeCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true); setError(null);
    try {
      const [dashRes, invRes, refRes] = await Promise.all([
        authFetch('/api/portal/dashboard'),
        authFetch('/api/portal/invoices'),
        authFetch('/api/user/referral'),
      ]);
      if (dashRes.ok) {
        const d = await dashRes.json();
        setStats(d.stats || { total: 0, active: 0, completed: 0, non_served: 0, success_rate: 0 });
        setRecentJobs(d.recent_jobs || []);
      }
      if (invRes.ok) {
        const d = await invRes.json();
        const unpaid = (d.invoices || []).filter(i => parseFloat(i.balance_due || 0) > 0);
        setOutstanding({ total: unpaid.reduce((s, i) => s + parseFloat(i.balance_due || 0), 0), count: unpaid.length });
      }
      if (refRes.ok) setReferral(await refRes.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const r = await authFetch('/api/portal/activity?limit=5');
      if (r.ok) setRecentActivity((await r.json()).activities || []);
    } finally { setActivityLoading(false); }
  };

  useEffect(() => { fetchAll(); fetchActivity(); /* eslint-disable-next-line */ }, []);

  const copyReferralCode = () => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/register?ref=${referral.referral_code}`);
    setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000);
  };

  const statusColor = (s) => ({ Served: 'var(--pv2-green-text)', Attempted: 'var(--pv2-amber-text)', 'Non-Service': 'var(--pv2-red-bright)' }[s] || '#60a5fa');
  const displayStatus = (j) => {
    const hasAttempts = j?.attempts && j.attempts.length > 0;
    if (j?.archived_at && !hasAttempts && !j.service_status) return 'Closed';
    return j.service_status || (j.archived_at ? 'Closed' : 'In Progress');
  };

  const ActivityIcon = ({ a }) => {
    if (a.type === 'received') return <Inbox size={14} strokeWidth={1.5} color="#60a5fa" />;
    if (a.success) return <CheckCircle size={14} strokeWidth={1.5} color="var(--pv2-green-text)" />;
    return <XCircle size={14} strokeWidth={1.5} color="var(--pv2-amber-text)" />;
  };

  return (
    <PortalV2Layout
      eyebrow={`Welcome back`}
      title={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Dashboard'}
      subtitle={user?.company_name}
      crumbs={<strong>Dashboard</strong>}
      action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchAll} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-dash">
            <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
          </button>
          <Link to="/portal/submit" className="pv2-btn pv2-btn-primary" data-testid="pv2-new-job">
            <PlusCircle size={14} strokeWidth={1.5} /> New Job
          </Link>
        </div>
      }
    >
      {error && <div className="pv2-alert pv2-alert-amber"><AlertCircle size={14} /><span>{error}</span></div>}

      {/* Outstanding + Referrals row */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 32 }}>
        {outstanding.total > 0 ? (
          <div className="pv2-card" style={{ padding: 22 }} data-testid="pv2-outstanding-balance">
            <div style={{ marginBottom: 14 }}>
              <div className="pv2-mono-tag" style={{ color: 'var(--pv2-amber-text)' }}>Outstanding Balance</div>
              <div style={{ fontSize: 28, fontFamily: 'Playfair Display, serif', fontWeight: 700, color: 'var(--pv2-amber-text)', marginTop: 6 }}>{fmtUSD(outstanding.total)}</div>
              <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 2 }}>{outstanding.count} unpaid invoice{outstanding.count !== 1 ? 's' : ''}</div>
            </div>
            <Link to="/portal/invoices" className="pv2-btn pv2-btn-primary" style={{ width: '100%' }} data-testid="pv2-view-pay">
              <CreditCard size={14} strokeWidth={1.5} /> View &amp; Pay
            </Link>
          </div>
        ) : (
          <div className="pv2-card" style={{ padding: 22 }} data-testid="pv2-no-balance">
            <div className="pv2-mono-tag" style={{ color: 'var(--pv2-green-text)' }}>Account Status</div>
            <div style={{ fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, marginTop: 6 }}>All Paid Up!</div>
            <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)', marginTop: 2 }}>No outstanding invoices</div>
          </div>
        )}

        {referral.referral_code && (
          <div className="pv2-card" style={{ padding: 22 }} data-testid="pv2-referral-card">
            <div style={{ marginBottom: 12 }}>
              <div className="pv2-mono-tag" style={{ color: 'var(--pv2-green-text)' }}>Referrals</div>
              <div style={{ fontSize: 18, fontFamily: 'Playfair Display, serif', fontWeight: 700, marginTop: 6 }}>Earn $25 per referral</div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--pv2-text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
              Share your code with other law firms. When they register and submit their first job, you&apos;ll receive a $25 credit automatically applied to your next invoice.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, fontSize: 12, color: 'var(--pv2-green-text)' }}>
              <span>Available: <strong style={{ marginLeft: 4 }}>${(referral.referral_credit || 0).toFixed(2)}</strong></span>
              <span>Total Saved: <strong style={{ marginLeft: 4 }}>${(referral.total_saved || 0).toFixed(2)}</strong></span>
              <span><strong style={{ marginRight: 4 }}>{referral.total_referrals || 0}</strong> Referrals</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--pv2-ink-line)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                <span style={{ color: 'var(--pv2-text-subtle)' }}>Code: </span>
                <strong style={{ color: 'var(--pv2-green-text)' }}>{referral.referral_code}</strong>
              </div>
              <button onClick={copyReferralCode} className="pv2-btn pv2-btn-primary" data-testid="pv2-copy-ref">
                <Copy size={13} strokeWidth={1.5} /> {codeCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Stat cards (5-up, force one row) */}
      <section className="pv2-stats-grid pv2-stats-grid--5" style={{ marginBottom: 32 }}>
        <div className="pv2-stat" data-testid="pv2-stat-total"><div className="pv2-stat-label">Total Jobs</div><div className="pv2-stat-value" style={{ fontSize: 32 }}><AnimatedNumber value={stats.total} /></div><div className="pv2-stat-meta">ALL SUBMITTED</div></div>
        <div className="pv2-stat" data-testid="pv2-stat-served"><div className="pv2-stat-label" style={{ color: 'var(--pv2-green-text)' }}>Served</div><div className="pv2-stat-value" style={{ fontSize: 32 }}><AnimatedNumber value={stats.completed} /></div><div className="pv2-stat-meta">SUCCESSFULLY COMPLETED</div></div>
        <div className="pv2-stat" data-testid="pv2-stat-active"><div className="pv2-stat-label" style={{ color: '#60a5fa' }}>Active</div><div className="pv2-stat-value" style={{ fontSize: 32 }}><AnimatedNumber value={stats.active} /></div><div className="pv2-stat-meta">IN PROGRESS</div></div>
        <div className="pv2-stat" data-testid="pv2-stat-non"><div className="pv2-stat-label" style={{ color: 'var(--pv2-red-bright)' }}>Non Served</div><div className="pv2-stat-value" style={{ fontSize: 32 }}><AnimatedNumber value={stats.non_served} /></div><div className="pv2-stat-meta">UNABLE TO COMPLETE</div></div>
        <div className="pv2-stat" data-testid="pv2-stat-rate"><div className="pv2-stat-label" style={{ color: 'var(--pv2-green-text)' }}>Success Rate</div><div className="pv2-stat-value" style={{ fontSize: 32, color: 'var(--pv2-green-text)' }}><AnimatedNumber value={stats.success_rate} suffix="%" /></div><div className="pv2-stat-meta">JOBS SERVED</div></div>
      </section>

      {/* Jobs map (only renders when the client has geocoded jobs) */}
      <ClientJobsMap />

      {/* Recent Jobs */}
      <section className="pv2-card" style={{ marginBottom: 24 }}>
        <div className="pv2-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="pv2-card-title">Recent Jobs</h2>
          <Link to="/portal/jobs" className="pv2-mono-sm" style={{ color: 'var(--pv2-red-bright)', display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="pv2-view-all-jobs">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--pv2-text-muted)' }}>Loading…</div>
          : recentJobs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <FileText size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div style={{ fontSize: 14, color: 'var(--pv2-text-muted)' }}>No jobs yet</div>
              <div style={{ fontSize: 12, color: 'var(--pv2-text-subtle)', marginTop: 4 }}>Submit your first job to get started</div>
              <Link to="/portal/submit" className="pv2-btn pv2-btn-primary" style={{ marginTop: 16 }}>
                <PlusCircle size={14} strokeWidth={1.5} /> Submit New Job
              </Link>
            </div>
          ) : (
            <div>
              {recentJobs.map((j, idx) => (
                <Link key={j.id || idx} to={`/portal/jobs/${j.servemanager_job_id || j.id}`}
                  data-testid={`pv2-recent-job-${idx}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid var(--pv2-ink-line-soft)', textDecoration: 'none', color: 'inherit', opacity: j.archived_at ? 0.7 : 1, ...(j.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : {}) }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor(j.service_status), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--pv2-text)' }}>{j.recipient_name || 'Untitled Job'}</span>
                      <span className="pv2-pill" style={{ color: statusColor(j.service_status) }}>
                        {displayStatus(j)}
                      </span>
                      {j.archived_at && <span className="pv2-pill" style={{ color: 'var(--pv2-text-muted)' }}><Archive size={9} strokeWidth={1.5} /> Archived</span>}
                    </div>
                    {j.case_title && <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 4, fontStyle: 'italic' }}>{j.case_title}</div>}
                    <div style={{ display: 'flex', gap: 14, marginTop: 4, fontSize: 11, color: 'var(--pv2-text-muted)' }}>
                      {j.address?.city && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={10} strokeWidth={1.5} /> {j.address.city}, {j.address.state}</span>}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} strokeWidth={1.5} /> {fmtDate(j.created_at)}</span>
                    </div>
                  </div>
                  <ArrowRight size={14} color="var(--pv2-text-subtle)" />
                </Link>
              ))}
            </div>
          )}
      </section>

      {/* Recent Activity */}
      <section className="pv2-card">
        <div className="pv2-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="pv2-card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Activity size={14} strokeWidth={1.5} color="var(--pv2-text-subtle)" /> Recent Activity
          </h2>
          <Link to="/portal/activity" className="pv2-mono-sm" style={{ color: 'var(--pv2-red-bright)', display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="pv2-view-all-activity">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {activityLoading ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--pv2-text-muted)' }}>Loading…</div>
          : recentActivity.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Activity size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div style={{ fontSize: 13, color: 'var(--pv2-text-muted)' }}>No recent activity</div>
            </div>
          ) : (
            <div>
              {recentActivity.map((a, i) => (
                <Link key={i} to={`/portal/jobs/${a.job?.job_id}`}
                  data-testid={`pv2-activity-${i}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--pv2-ink-line-soft)', textDecoration: 'none', color: 'inherit', ...(a.job?.is_rush ? { background: 'rgba(248, 113, 113, 0.07)', boxShadow: 'inset 3px 0 0 0 var(--pv2-red-bright)' } : {}) }}>
                  <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', flexShrink: 0 }}>
                    <ActivityIcon a={a} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{a.job?.recipient_name}</div>
                    {a.job?.case_title && <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', fontStyle: 'italic' }}>{a.job.case_title}</div>}
                    <div style={{ fontSize: 11, color: 'var(--pv2-text-muted)' }}>{a.type === 'received' ? 'Documents received' : a.success ? 'Successfully served' : a.serve_type || 'Attempt made'}</div>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--pv2-text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}>{fmtDate(a.timestamp)}</span>
                </Link>
              ))}
            </div>
          )}
      </section>
    </PortalV2Layout>
  );
};

export default ClientV2Dashboard;
